import { tassign } from 'tassign';

// appConfig
import { appConfig } from '../../config/app-config';

import { IUserState, USER_INITIAL_STATE, userReducer } from '../../user/store/store';
import { IMenuState, MENU_INITIAL_STATE, menuReducer } from '../../menu/store/store';
import { IMangelState, MANGEL_INITIAL_STATE, mangelReducer } from '../../maengel/store/store';
import { INavigationState, NAVIGATION_INITIAL_STATE, navigationReducer } from '../../nav/store/store';
import { IUiState, UI_INITIAL_STATE, uiReducer } from '../components/message-snack/store/store';
import { IBreadCrumbState, BREADCRUMB_INITIAL_STATE, breadcrumbReducer } from '../components/breadcrumb/store/store';

// ------------------------------------------------------------------------------------------------
// -- User Actions --------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   USER_CHANGE_CURRENT,
   BACKEND_VERSION,
   USER_ROLES
} from '../../user/store/actions';

// ------------------------------------------------------------------------------------------------
// -- Menu Actions --------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   MENU_WANT_PROTECTED_ROUTE,
   MENU_SHOW_NAV_SEARCH
} from '../../menu/store/actions';

// ------------------------------------------------------------------------------------------------
// -- Ui Actions ----------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   UI_MESSAGE_SNACK,
   UI_MESSAGE_SNACK_RESET
} from '../../core/components/message-snack/store/actions';

// ------------------------------------------------------------------------------------------------
// -- Mangel Actions ------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   // -- 1. Data from Backend -------------------
   MANGEL_STORE_OBJECTS,

   // -- 2. Reset State -------------------------
   MANGEL_RESET_STATE_ALL,

   // -- 3. Selection ---------------------------
   MANGEL_SELECT_ITEM,

   // -- 4. Item Management ---------------------
   MANGEL_SET_TODELETE,
   MANGEL_SET_TOADD,
   MANGEL_SET_TOEDIT,
   MANGEL_ADD_ITEM,
   MANGEL_DELETE_ITEM,
   MANGEL_EDIT_ITEM,

   // -- 5. Elemente ----------------------------
   MANGEL_STORE_DETAIL_JSS
} from '../../maengel/store/actions';

// ------------------------------------------------------------------------------------------------
// -- Navigation Actions --------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   // -- 0. Reset State -------------------------
   NAVIGATION_RESET_STATE_ALL_EXCEPT_CONFIG,

   // -- 1. Config ------------------------------
   NAVIGATION_STORE_CONFIG,

   // -- 2. Element Graph -----------------------
   NAVIGATION_STORE_RESULT_1ST_INITIAL,
   NAVIGATION_STORE_RESULT_1ST_EXTENDED,
   NAVIGATION_STORE_RESULT_1ST_DETAIL,
   NAVIGATION_STORE_1ST_DISPLAY_OPTIONS,
   NAVIGATION_STORE_1ST_REQUEST_OPTIONS,
   NAVIGATION_STORE_1ST_AVAILABLE_EDGEGROUP_PAIRS,
   NAVIGATION_STORE_1ST_DISPLAY_OPTION_EDGES,

   // -- 3. Search Graph ------------------------
   NAVIGATION_STORE_RESULT_2ND_INITIAL,
   NAVIGATION_STORE_RESULT_2ND_EXTENDED,
   NAVIGATION_STORE_RESULT_2ND_DETAIL,
   NAVIGATION_STORE_2ND_DISPLAY_OPTIONS,
   NAVIGATION_STORE_2ND_REQUEST_OPTIONS,
   NAVIGATION_STORE_2ND_AVAILABLE_EDGEGROUP_PAIRS,
   NAVIGATION_STORE_2ND_DISPLAY_OPTION_EDGES,
   NAVIGATION_STORE_2ND_EDGE_DEFINITION_CONFIG,

   // -- 4. Schema ------------------------------
   NAVIGATION_STORE_SCHEMA
} from '../../nav/store/actions';

// ------------------------------------------------------------------------------------------------
// -- Breadcrumb Actions --------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
import {
   BREADCRUMB_RESET_STATE,

   BREADCRUMB_SET_INITIAL_CRUMB,
   BREADCRUMB_ADD_CRUMB,
   BREADCRUMB_ADD_CRUMB_AND_SHRING,
   BREADCRUMB_SHRINK_TO_CRUMB_FROM_START,

   BREADCRUMB_SET_SELECTED_CRUMB
} from '../../core/components/breadcrumb/store/actions';

// ------------------------------------------------------------------------------------------------
// -- IAppState -----------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
export interface IAppState {
   userState: IUserState;
   menuState: IMenuState;
   mangelState: IMangelState;
   navigationState: INavigationState;
   uiState: IUiState;
   breadcrumbState: IBreadCrumbState;
}

// ------------------------------------------------------------------------------------------------
// -- INITIAL_STATE -------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
export const INITIAL_STATE: IAppState = {
   userState: USER_INITIAL_STATE,
   menuState: MENU_INITIAL_STATE,
   mangelState: MANGEL_INITIAL_STATE,
   navigationState: NAVIGATION_INITIAL_STATE,
   uiState: UI_INITIAL_STATE,
   breadcrumbState: BREADCRUMB_INITIAL_STATE
};

// ------------------------------------------------------------------------------------------------
// -- rootReducer ---------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
export function rootReducer(state: IAppState = INITIAL_STATE, action): IAppState {
   // dispatch
   switch (action.type) {
      // ----------------------------------------------------------------------------------------
      // -- User --------------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      // -- 1. Data from Backend --------------
      case USER_CHANGE_CURRENT:
      case BACKEND_VERSION:
      case USER_ROLES:
         return tassign(state, { userState: userReducer(state.userState, action) });

      // ----------------------------------------------------------------------------------------
      // -- Menu --------------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      case MENU_WANT_PROTECTED_ROUTE:
      case MENU_SHOW_NAV_SEARCH:
         return tassign(state, { menuState: menuReducer(state.menuState, action) });

      // ----------------------------------------------------------------------------------------
      // -- Ui ----------------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      case UI_MESSAGE_SNACK:
      case UI_MESSAGE_SNACK_RESET:
         return tassign(state, { uiState: uiReducer(state.uiState, action) });

      // ----------------------------------------------------------------------------------------
      // -- Mangel ------------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      case MANGEL_STORE_OBJECTS:
      case MANGEL_RESET_STATE_ALL:
      case MANGEL_SELECT_ITEM:
      case MANGEL_SET_TODELETE:
      case MANGEL_SET_TOADD:
      case MANGEL_SET_TOEDIT:
      case MANGEL_ADD_ITEM:
      case MANGEL_DELETE_ITEM:
      case MANGEL_EDIT_ITEM:
      case MANGEL_STORE_DETAIL_JSS:
         return tassign(state, { mangelState: mangelReducer(state.mangelState, action) });

      // ----------------------------------------------------------------------------------------
      // -- Navigation --------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      case NAVIGATION_RESET_STATE_ALL_EXCEPT_CONFIG:
      case NAVIGATION_STORE_CONFIG:
      // graph 1
      case NAVIGATION_STORE_RESULT_1ST_INITIAL:
      case NAVIGATION_STORE_RESULT_1ST_EXTENDED:
      case NAVIGATION_STORE_RESULT_1ST_DETAIL:
      case NAVIGATION_STORE_1ST_DISPLAY_OPTIONS:
      case NAVIGATION_STORE_1ST_REQUEST_OPTIONS:
      case NAVIGATION_STORE_1ST_AVAILABLE_EDGEGROUP_PAIRS:
      case NAVIGATION_STORE_1ST_DISPLAY_OPTION_EDGES:
      // graph 2
      case NAVIGATION_STORE_RESULT_2ND_INITIAL:
      case NAVIGATION_STORE_RESULT_2ND_EXTENDED:
      case NAVIGATION_STORE_RESULT_2ND_DETAIL:
      case NAVIGATION_STORE_2ND_DISPLAY_OPTIONS:
      case NAVIGATION_STORE_2ND_REQUEST_OPTIONS:
      case NAVIGATION_STORE_2ND_AVAILABLE_EDGEGROUP_PAIRS:
      case NAVIGATION_STORE_2ND_DISPLAY_OPTION_EDGES:
      case NAVIGATION_STORE_2ND_EDGE_DEFINITION_CONFIG:
      // schema
      case NAVIGATION_STORE_SCHEMA:
         return tassign(state, { navigationState: navigationReducer(state.navigationState, action) });

      // ----------------------------------------------------------------------------------------
      // -- Breadcrumb --------------------------------------------------------------------------
      // ----------------------------------------------------------------------------------------
      case BREADCRUMB_RESET_STATE:
      case BREADCRUMB_SET_INITIAL_CRUMB:
      case BREADCRUMB_ADD_CRUMB:
      case BREADCRUMB_ADD_CRUMB_AND_SHRING:
      case BREADCRUMB_SHRINK_TO_CRUMB_FROM_START:
      case BREADCRUMB_SET_SELECTED_CRUMB:
         return tassign(state, { breadcrumbState: breadcrumbReducer(state.breadcrumbState, action) });
   }

   return state;
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/core/store/store.ts